import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/client";
import ConfirmDialog from "../common/ConfirmDialog";
import { createClientId } from "../../utils/id";

export default function StageConfigPanel({ node, onSave, onDelete, onClose }) {
  const [form, setForm] = useState(null);
  const [addingTask, setAddingTask] = useState(false);
  const [fileTypeMenuId, setFileTypeMenuId] = useState(null);
  const [hoveredTaskType, setHoveredTaskType] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const [rolesList, setRolesList] = useState([]);

  const TASK_TYPES = [
    {
      type: "text",
      icon: "✏️",
      label: "Text Field",
      desc: "Free-form text response",
    },
    {
      type: "file",
      icon: "📎",
      label: "File Upload",
      desc: "Upload a document or image",
    },
    {
      type: "checkbox",
      icon: "☑️",
      label: "Checkbox",
      desc: "Single yes/no confirmation",
    },
  ];

  const FILE_TYPES = useMemo(
    () => [
      { value: "pdf", label: "PDF" },
      { value: "jpg", label: "JPG" },
      { value: "png", label: "PNG" },
      { value: "doc", label: "DOC" },
      { value: "docx", label: "DOCX" },
      { value: "xlsx", label: "XLSX" },
    ],
    [],
  );

  const fileTypeLabelByValue = useMemo(() => {
    const map = {};
    FILE_TYPES.forEach((t) => {
      map[t.value] = t.label;
    });
    return map;
  }, [FILE_TYPES]);

  useEffect(() => {
    if (node) {
      setForm({ ...node.data, id: node.id });
    } else {
      setForm(null);
    }
  }, [node]);

  useEffect(() => {
    // load roles once
    api
      .get("/roles")
      .then((r) => setRolesList(r.data))
      .catch((e) => console.error("failed to load roles", e));
  }, []);

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleSave = () => {
    if (onSave) onSave({ id: form.id, data: { ...form } });
    handleClose();
  };

  const handleDelete = () => {
    setDeleteConfirm(true);
  };

  if (!node || !form) return null;

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const sectionStyle = {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: 12,
    background: "#f9fafb",
  };

  const labelStyle = { display: "block", fontSize: 12, color: "#4b5563" };
  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#fff",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.35)",
        zIndex: 100000,
        padding: 16,
        pointerEvents: "none",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width: "min(900px, 96%)",
          maxHeight: "90vh",
          overflowY: "auto",
          background: "#fff",
          borderRadius: 14,
          padding: 18,
          boxShadow: "0 18px 40px rgba(15,23,42,0.18)",
          border: "1px solid #e5e7eb",
          pointerEvents: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 999,
                background: form.color || "#6366f1",
              }}
            />
            <div>
              <h3 style={{ margin: 0, fontSize: 18 }}>
                {form.name || "Stage"}
              </h3>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Configure stage settings, assignments, and tasks
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClose();
            }}
            style={{
              border: "1px solid #e5e7eb",
              background: "#fff",
              borderRadius: 8,
              padding: "6px 10px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 12,
            }}
          >
            Close
          </button>
        </div>

        <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Basics</div>
            <label style={labelStyle}>Name</label>
            <input
              value={form.name || ""}
              onChange={(e) => update({ name: e.target.value })}
              style={inputStyle}
            />

            <label style={{ ...labelStyle, marginTop: 8 }}>Description</label>
            <textarea
              value={form.description || ""}
              onChange={(e) => update({ description: e.target.value })}
              style={{ ...inputStyle, minHeight: 70, resize: "vertical" }}
            />

            <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Color</label>
                <input
                  type="color"
                  value={form.color || "#6366f1"}
                  onChange={(e) => update({ color: e.target.value })}
                  style={{ width: 60, height: 34, border: "none" }}
                />
              </div>
              {/* <div style={{ flex: 1 }}>
                <label style={labelStyle}>Deadline Type</label>
                <select
                  value={form.deadlineType || "None"}
                  onChange={(e) => update({ deadlineType: e.target.value })}
                  style={inputStyle}
                >
                  <option>None</option>
                  <option>Fixed Date</option>
                  <option>Relative to Previous Stage</option>
                </select>
              </div> */}
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Assignments</div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {/* <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={!!form.isReassignable}
                  onChange={(e) => update({ isReassignable: e.target.checked })}
                />
                <span style={{ fontSize: 13, color: "#111827" }}>
                  Reassignable
                </span>
              </label> */}
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={form.sendForReview !== false}
                  onChange={(e) => update({ sendForReview: e.target.checked })}
                />
                <span style={{ fontSize: 13, color: "#111827" }}>
                  Send For Review
                </span>
              </label>
            </div>

            <div style={{ marginTop: 10 }}>
              <label style={labelStyle}>
                Branching (when this stage has 2+ outgoing connections)
              </label>
              <select
                value={form.branchMode === "PARALLEL" ? "PARALLEL" : "CHOICE"}
                onChange={(e) => update({ branchMode: e.target.value })}
                style={{ width: "100%" }}
              >
                <option value="CHOICE">
                  Choice — reviewer picks one branch
                </option>
                <option value="PARALLEL">
                  Parallel — all branches start together
                </option>
              </select>
            </div>

            <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
              <div style={{ width: 200 }}>
                <label style={labelStyle}>Max Concurrent Assignees</label>
                <input
                  type="number"
                  min={1}
                  value={form.maxConcurrentAssignees || 1}
                  onChange={(e) =>
                    update({
                      maxConcurrentAssignees: Number(e.target.value) || 1,
                    })
                  }
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Eligible Roles for this Stage</label>
                <select
                  multiple
                  value={form.assigneeRoles || []}
                  onChange={(e) => {
                    const opts = Array.from(e.target.selectedOptions).map(
                      (o) => o.value,
                    );
                    update({ assigneeRoles: opts });
                  }}
                  style={{ ...inputStyle, height: 80 }}
                >
                  {rolesList.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                  Leave empty for no restriction
                </div>
              </div>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>
              Timeline & SLA
            </div>
            <label style={labelStyle}>Estimated Duration</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="number"
                min={0}
                value={form.estimatedDuration || 0}
                onChange={(e) =>
                  update({ estimatedDuration: Number(e.target.value) || 0 })
                }
                style={{ ...inputStyle, width: 120 }}
              />
              <select
                value={form.durationUnit || "Days"}
                onChange={(e) => update({ durationUnit: e.target.value })}
                style={{ ...inputStyle, width: 140 }}
              >
                <option>Minutes</option>
                <option>Hours</option>
                <option>Days</option>
                <option>Weeks</option>
              </select>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Tasks</div>
            <div style={{ marginTop: 8 }}>
              {(form.tasks || []).map((t, i) => {
                const selected = t.fileTypes || [];
                const selectedLabel = selected.length
                  ? selected.map((v) => fileTypeLabelByValue[v] || v).join(", ")
                  : "Any file type";
                const taskTypeMeta =
                  TASK_TYPES.find((x) => x.type === (t.type || "text")) ||
                  TASK_TYPES[0];
                return (
                  <div
                    key={t.id || i}
                    style={{
                      border: "1px solid #e5e7eb",
                      marginBottom: 10,
                      borderRadius: 10,
                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px 12px",
                        background: "#f8faff",
                        borderBottom: "1px solid #e5e7eb",
                        borderTopLeftRadius: 10,
                        borderTopRightRadius: 10,
                      }}
                    >
                      <div style={{ display: "flex", gap: 10 }}>
                        <div style={{ fontSize: 18, lineHeight: "22px" }}>
                          {taskTypeMeta.icon}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700 }}>
                            {taskTypeMeta.label}
                          </div>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {taskTypeMeta.desc}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const next = (form.tasks || []).slice();
                          next.splice(i, 1);
                          update({ tasks: next });
                        }}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "#ef4444",
                          fontSize: 12,
                          cursor: "pointer",
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    <div style={{ padding: "10px 12px" }}>
                      <label style={labelStyle}>Title</label>
                      <input
                        value={t.title || ""}
                        onChange={(e) => {
                          const next = (form.tasks || []).map((x, idx) =>
                            idx === i ? { ...x, title: e.target.value } : x,
                          );
                          update({ tasks: next });
                        }}
                        style={inputStyle}
                      />

                      {t.type === "checkbox" && (
                        <>
                          <label style={{ ...labelStyle, marginTop: 8 }}>
                            Checkbox Label
                          </label>
                          <input
                            value={t.checkboxLabel || ""}
                            onChange={(e) => {
                              const next = (form.tasks || []).map((x, idx) =>
                                idx === i
                                  ? { ...x, checkboxLabel: e.target.value }
                                  : x,
                              );
                              update({ tasks: next });
                            }}
                            style={inputStyle}
                          />
                        </>
                      )}

                      <label style={{ ...labelStyle, marginTop: 8 }}>
                        Required
                      </label>
                      <input
                        type="checkbox"
                        checked={!!t.mandatory}
                        onChange={(e) => {
                          const next = (form.tasks || []).map((x, idx) =>
                            idx === i
                              ? { ...x, mandatory: e.target.checked }
                              : x,
                          );
                          update({ tasks: next });
                        }}
                      />

                      {t.type === "file" && (
                        <>
                          <label style={{ ...labelStyle, marginTop: 8 }}>
                            Allowed File Types
                          </label>
                          <div style={{ position: "relative" }}>
                            <button
                              type="button"
                              onClick={() =>
                                setFileTypeMenuId(
                                  fileTypeMenuId === (t.id || i)
                                    ? null
                                    : t.id || i,
                                )
                              }
                              style={{
                                ...inputStyle,
                                textAlign: "left",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                cursor: "pointer",
                              }}
                            >
                              <span>{selectedLabel}</span>
                              <span style={{ color: "#9ca3af" }}>v</span>
                            </button>
                            {fileTypeMenuId === (t.id || i) && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "calc(100% + 6px)",
                                  left: 0,
                                  width: "100%",
                                  background: "#fff",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 8,
                                  boxShadow: "0 10px 25px rgba(15,23,42,0.12)",
                                  padding: 8,
                                  zIndex: 20,
                                }}
                              >
                                <label
                                  style={{
                                    display: "flex",
                                    gap: 8,
                                    alignItems: "center",
                                    padding: "4px 6px",
                                    cursor: "pointer",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selected.length === 0}
                                    onChange={() => {
                                      const next = (form.tasks || []).map(
                                        (x, idx) =>
                                          idx === i
                                            ? { ...x, fileTypes: [] }
                                            : x,
                                      );
                                      update({ tasks: next });
                                    }}
                                  />
                                  <span style={{ fontSize: 13 }}>Any</span>
                                </label>
                                {FILE_TYPES.map((ft) => (
                                  <label
                                    key={ft.value}
                                    style={{
                                      display: "flex",
                                      gap: 8,
                                      alignItems: "center",
                                      padding: "4px 6px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selected.includes(ft.value)}
                                      onChange={(e) => {
                                        const nextTypes = e.target.checked
                                          ? Array.from(
                                              new Set(
                                                selected.concat(ft.value),
                                              ),
                                            )
                                          : selected.filter(
                                              (v) => v !== ft.value,
                                            );
                                        const next = (form.tasks || []).map(
                                          (x, idx) =>
                                            idx === i
                                              ? { ...x, fileTypes: nextTypes }
                                              : x,
                                        );
                                        update({ tasks: next });
                                      }}
                                    />
                                    <span style={{ fontSize: 13 }}>
                                      {ft.label}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

              {!addingTask && (
                <button
                  onClick={() => setAddingTask(true)}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: "1px dashed #cbd5f5",
                    background: "#eef2ff",
                    color: "#3730a3",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  + Add Task
                </button>
              )}
              {addingTask && (
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 12,
                    background: "#fff",
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 10 }}>
                    Choose task type
                  </div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {TASK_TYPES.map((type) => {
                      const isHovered = hoveredTaskType === type.type;
                      return (
                        <button
                          key={type.type}
                          type="button"
                          onMouseEnter={() => setHoveredTaskType(type.type)}
                          onMouseLeave={() => setHoveredTaskType(null)}
                          onClick={() => {
                            const id = createClientId("task");
                            const nextTask =
                              type.type === "checkbox"
                                ? {
                                    id,
                                    type: "checkbox",
                                    title: "",
                                    mandatory: false,
                                    checkboxLabel: "I confirm this is done",
                                  }
                                : {
                                    id,
                                    type: type.type,
                                    title: "",
                                    mandatory: false,
                                    fileTypes: [],
                                  };
                            const next = (form.tasks || []).concat(nextTask);
                            update({ tasks: next });
                            setAddingTask(false);
                          }}
                          style={{
                            width: 140,
                            textAlign: "left",
                            padding: 10,
                            borderRadius: 10,
                            border: `1px solid ${
                              isHovered ? "#6366f1" : "#e5e7eb"
                            }`,
                            background: isHovered ? "#eef2ff" : "#fff",
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <div style={{ fontSize: 20, marginBottom: 6 }}>
                            {type.icon}
                          </div>
                          <div style={{ fontWeight: 700 }}>{type.label}</div>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {type.desc}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: 10,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setAddingTask(false)}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#6b7280",
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              marginTop: 4,
              display: "flex",
              gap: 8,
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSave();
              }}
              style={{
                padding: "10px 14px",
                background: "#4f46e5",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Save Changes
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDelete();
              }}
              style={{
                padding: "10px 14px",
                borderRadius: 8,
                border: "1px solid #e5e7eb",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
      {deleteConfirm && (
        <ConfirmDialog
          title="Delete Stage"
          message={`Delete stage "${form?.name || "this stage"}"? This cannot be undone.`}
          confirmLabel="Delete"
          confirmClass="btn-danger"
          onConfirm={() => { setDeleteConfirm(false); if (onDelete) onDelete(form.id); handleClose(); }}
          onClose={() => setDeleteConfirm(false)}
        />
      )}
    </div>
  );
}
